import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bars3Icon, 
  XMarkIcon,
  ChevronDownIcon,
  HomeIcon,
  InformationCircleIcon,
  BookOpenIcon,
  AcademicCapIcon,
  UsersIcon,
  EllipsisHorizontalIcon,
  BuildingOfficeIcon,
  PhotoIcon,
  DocumentTextIcon,
  FolderIcon
} from '@heroicons/react/24/outline';

import { useTheme } from '../contexts/ThemeContext';
import TranslatableText from './TranslatableText';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubItems, setActiveSubItems] = useState({}); // New state for subItems
  const location = useLocation();
  const { isDark } = useTheme();

  const navigationItems = [
    {
      name: 'Home',
      href: '/',
      icon: HomeIcon
    },
    {
      name: 'About',
      href: '#',
      icon: InformationCircleIcon,
      dropdown: [
        { name: 'About School', href: '/about' },
        { name: 'Principal Message', href: '/principal-message' },
        { name: 'Vision & Mission', href: '/vision-mission' },
        { name: 'School Management Committee', href: '/smc' },
        { name: 'Faculty Members', href: '/faculty' },
        // { name: 'History & Legacy', href: '/history' }
      ]
    },
    {
      name: 'Academics',
      href: '#',
      icon: BookOpenIcon,
      dropdown: [
        // { name: 'Academic Calendar', href: '/academic-calendar' },
        // { name: 'Subjects Offered', href: '/subjects' },
        // { name: 'Examination System', href: '/examination' },
        // { name: 'Academic Policies', href: '/academic-policies' },
        { name: 'Curriculum Overview', href: '/academics' }
      ]
    },
    {
      name: 'Admissions',
      href: '#',
      icon: AcademicCapIcon,
      dropdown: [
        { name: 'Admission Process', href: '/admissions' },
        { name: 'Fee Structure', href: '/fee-structure' }
        // { name: 'Eligibility Criteria', href: '/eligibility' },
        // { name: 'Online Application', href: '/apply-online' },
        // { name: 'Admission FAQs', href: '/admission-faqs' }
      ]
    },
    {
      name: 'Gallery',
      href: '#',
      icon: PhotoIcon,
      dropdown: [
        { name: 'Photo Gallery', href: '/gallery' },
        { name: 'Video Gallery', href: '/video-gallery' },
        // { name: 'Virtual Tour', href: '/virtual-tour' },
        { name: 'Events Gallery', href: '/events-gallery' }
      ]
    },
    {
      name: 'Student Life',
      href: '#',
      icon: UsersIcon,
      dropdown: [
        { name: 'Clubs & Societies', href: '/clubs' },
        { name: 'Sports & Games', href: '/sports' },
        { name: 'Cultural Programs', href: '/cultural' }
      ]
    },
    {
      name: 'More',
      href: '#',
      icon: EllipsisHorizontalIcon,
      dropdown: [
        { name: 'Infrastructure', href: '/laboratories', category: 'facilities', icon: BuildingOfficeIcon, subItems: [
          { name: 'Laboratories', href: '/laboratories' },
          { name: 'Library', href: '/library' },
          { name: 'Sports Facilities', href: '/sports-facilities' },
          // { name: 'Transport', href: '/transport' }
        ]},
        { name: 'Information', href: '/achievements', category: 'info', icon: DocumentTextIcon, subItems: [
          { name: 'Achievements', href: '/achievements' },
          { name: 'News & Updates', href: '/news' }
        ]},
        { name: 'Resources', href: '/downloads', category: 'resources', icon: FolderIcon, subItems: [
          { name: 'Downloads', href: '/downloads' },
          { name: 'Public Disclosure', href: '/public-disclosure' },
          { name: 'Public Disclosure SARAS PDF', href: '/files/Mandatory Disclosure Details _ SARAS 6.0.pdf' },
          { name: 'Contact Us', href: '/contact' }
        ]}
      ]
    }
  ];

  const isActivePath = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const toggleDropdown = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  const toggleSubItems = (dropItemName) => {
    setActiveSubItems(prev => ({
      ...prev,
      [dropItemName]: !prev[dropItemName]
    }));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdown(null);
      setActiveSubItems({}); // Reset subItems when closing
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <motion.nav
      initial={{ y: -10 }}
      animate={{ y: 0 }}
      className={`sticky top-0 z-40 ${isDark ? 'bg-dark-surface border-dark-border' : 'bg-white border-gray-200'} border-b shadow-md`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          
          {/* Desktop Navigation - Traditional School Design */}
          <div className="hidden lg:flex items-center space-x-8 flex-1">
            {navigationItems.map((item, index) => (
              <div key={item.name} className="relative">
                {item.dropdown ? (
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDropdown(index);
                      }}
                      className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        item.dropdown.some(dropItem => isActivePath(dropItem.href))
                          ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'
                          : isDark
                            ? 'text-dark-text hover:text-blue-400 hover:bg-blue-900/20'
                            : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                      }`}
                    >
                      <item.icon className="w-4 h-4 mr-2" />
                      <TranslatableText text={item.name} />
                      <ChevronDownIcon className={`w-4 h-4 ml-1 transition-transform ${
                        activeDropdown === index ? 'rotate-180' : ''
                      }`} />
                    </button>
                    
                    <AnimatePresence>
                      {activeDropdown === index && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className={`absolute top-full ${item.name === 'More' ? 'right-0 w-96' : 'left-0 w-64'} mt-1 rounded-lg shadow-xl border py-2 z-50 ${
                            isDark 
                              ? 'bg-dark-surface border-dark-border' 
                              : 'bg-white border-gray-200'
                          }`}
                        >
                          {item.name === 'More' ? (
                            /* Special grid layout for More menu */
                            <div className="grid grid-cols-2 gap-2 p-3">
                              {item.dropdown.map((dropItem) => (
                                <div key={dropItem.name} className={`rounded-lg p-3 border transition-all ${
                                  isDark ? 'border-dark-border hover:bg-dark-card hover:border-blue-500/30' : 'border-gray-100 hover:bg-blue-50 hover:border-blue-200'
                                }`}>
                                  {dropItem.href.endsWith('.pdf') ? (
                                    <a
                                      href={dropItem.href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`block font-medium text-sm mb-2 transition-colors flex items-center ${
                                        isActivePath(dropItem.href)
                                          ? 'text-blue-600'
                                          : isDark
                                            ? 'text-dark-text hover:text-blue-400'
                                            : 'text-gray-800 hover:text-blue-600'
                                      }`}
                                      onClick={() => setActiveDropdown(null)}
                                    >
                                      {dropItem.icon && <dropItem.icon className="w-4 h-4 mr-2" />}
                                      <TranslatableText text={dropItem.name} />
                                    </a>
                                  ) : (
                                    <Link
                                      to={dropItem.href}
                                      className={`block font-medium text-sm mb-2 transition-colors flex items-center ${
                                        isActivePath(dropItem.href)
                                          ? 'text-blue-600'
                                          : isDark
                                            ? 'text-dark-text hover:text-blue-400'
                                            : 'text-gray-800 hover:text-blue-600'
                                      }`}
                                      onClick={() => setActiveDropdown(null)}
                                    >
                                      {dropItem.icon && <dropItem.icon className="w-4 h-4 mr-2" />}
                                      <TranslatableText text={dropItem.name} />
                                    </Link>
                                  )}
                                  {dropItem.subItems && (
                                    <div className="space-y-1">
                                      {dropItem.subItems.map((subItem) => (
                                        subItem.href.endsWith('.pdf') ? (
                                          <a
                                            key={subItem.name}
                                            href={subItem.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`block text-xs py-1 px-2 rounded transition-colors ${
                                              isActivePath(subItem.href)
                                                ? 'text-blue-600 font-medium bg-blue-50 dark:bg-blue-900/30'
                                                : isDark
                                                  ? 'text-dark-text-secondary hover:text-blue-400 hover:bg-blue-900/20'
                                                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-100'
                                            }`}
                                            onClick={() => setActiveDropdown(null)}
                                          >
                                            <TranslatableText text={subItem.name} />
                                          </a>
                                        ) : (
                                          <Link
                                            key={subItem.name}
                                            to={subItem.href}
                                            className={`block text-xs py-1 px-2 rounded transition-colors ${
                                              isActivePath(subItem.href)
                                                ? 'text-blue-600 font-medium bg-blue-50 dark:bg-blue-900/30'
                                                : isDark
                                                  ? 'text-dark-text-secondary hover:text-blue-400 hover:bg-blue-900/20'
                                                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-100'
                                            }`}
                                            onClick={() => setActiveDropdown(null)}
                                          >
                                            <TranslatableText text={subItem.name} />
                                          </Link>
                                        )
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            /* Standard dropdown layout for other menus */
                            item.dropdown.map((dropItem) => (
                              <div key={dropItem.name}>
                                {dropItem.href.endsWith('.pdf') ? (
                                  <a
                                    href={dropItem.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`block px-4 py-3 text-sm transition-colors ${
                                      isActivePath(dropItem.href)
                                        ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'
                                        : isDark
                                          ? 'text-dark-text hover:text-blue-400 hover:bg-blue-900/20'
                                          : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                                    }`}
                                    onClick={() => setActiveDropdown(null)}
                                  >
                                    <TranslatableText text={dropItem.name} />
                                  </a>
                                ) : (
                                  <Link
                                    to={dropItem.href}
                                    className={`block px-4 py-3 text-sm transition-colors ${
                                      isActivePath(dropItem.href)
                                        ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'
                                        : isDark
                                          ? 'text-dark-text hover:text-blue-400 hover:bg-blue-900/20'
                                          : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                                    }`}
                                    onClick={() => setActiveDropdown(null)}
                                  >
                                    <TranslatableText text={dropItem.name} />
                                  </Link>
                                )}
                                {dropItem.subItems && (
                                  <div className="ml-4 space-y-1">
                                    {dropItem.subItems.map((subItem) => (
                                      subItem.href.endsWith('.pdf') ? (
                                        <a
                                          key={subItem.name}
                                          href={subItem.href}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`block px-4 py-2 text-xs transition-colors ${
                                            isActivePath(subItem.href)
                                              ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'
                                              : isDark
                                                ? 'text-dark-text-secondary hover:text-blue-400 hover:bg-blue-900/20'
                                                : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                                          }`}
                                          onClick={() => setActiveDropdown(null)}
                                        >
                                          • <TranslatableText text={subItem.name} />
                                        </a>
                                      ) : (
                                        <Link
                                          key={subItem.name}
                                          to={subItem.href}
                                          className={`block px-4 py-2 text-xs transition-colors ${
                                            isActivePath(subItem.href)
                                              ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'
                                              : isDark
                                                ? 'text-dark-text-secondary hover:text-blue-400 hover:bg-blue-900/20'
                                                : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                                          }`}
                                          onClick={() => setActiveDropdown(null)}
                                        >
                                          • <TranslatableText text={subItem.name} />
                                        </Link>
                                      )
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActivePath(item.href)
                        ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'
                        : isDark
                          ? 'text-dark-text hover:text-blue-400 hover:bg-blue-900/20'
                          : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    <item.icon className="w-4 h-4 mr-2" />
                    <TranslatableText text={item.name} />
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Apply Now Button - Traditional School Style */}
          <div className="hidden lg:block">
            <Link
              to="/admissions"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:shadow-lg transition-all duration-200 flex items-center"
            >
              <AcademicCapIcon className="w-4 h-4 mr-2" />
              <TranslatableText text="Apply Now" />
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center">
            <motion.button
              onClick={() => {
                setIsOpen(!isOpen);
                if (isOpen) {
                  // Reset states when closing mobile menu
                  setActiveDropdown(null);
                  setActiveSubItems({});
                }
              }}
              className={`p-2 rounded-md transition-colors ${
                isDark 
                  ? 'text-dark-text hover:text-blue-400 hover:bg-blue-900/20' 
                  : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
              }`}
              whileTap={{ scale: 0.95 }}
            >
              {isOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation - Traditional School Design */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`lg:hidden border-t ${
              isDark 
                ? 'bg-dark-surface border-dark-border' 
                : 'bg-white border-gray-200'
            }`}
          >
            <div className="px-4 py-4 space-y-2 max-h-96 overflow-y-auto">
              {navigationItems.map((item, index) => (
                <div key={item.name} className="touch-manipulation">
                  {item.dropdown ? (
                    <div>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleDropdown(index);
                        }}
                        className={`flex items-center justify-between w-full px-3 py-4 rounded-md text-sm font-medium transition-colors touch-manipulation ${
                          isDark
                            ? 'text-dark-text hover:text-blue-400 hover:bg-blue-900/20'
                            : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        <div className="flex items-center">
                          <item.icon className="w-5 h-5 mr-3" />
                          <TranslatableText text={item.name} />
                        </div>
                        <ChevronDownIcon className={`w-5 h-5 transition-transform ${
                          activeDropdown === index ? 'rotate-180' : ''
                        }`} />
                      </button>
                      {activeDropdown === index && (
                        <div className="ml-6 mt-2 space-y-1">
                          {item.dropdown.map((dropItem) => (
                            <div key={dropItem.name}>
                              {dropItem.subItems ? (
                                /* Handle dropdown items with subItems */
                                <div>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      toggleSubItems(dropItem.name);
                                    }}
                                    className={`flex items-center justify-between w-full px-4 py-4 rounded-md text-sm transition-colors touch-manipulation ${
                                      isActivePath(dropItem.href)
                                        ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'
                                        : isDark
                                          ? 'text-dark-text-secondary hover:text-blue-400 hover:bg-blue-900/20'
                                          : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                                    }`}
                                  >
                                    <div className="flex items-center">
                                      {dropItem.icon && <dropItem.icon className="w-4 h-4 mr-2" />}
                                      <TranslatableText text={dropItem.name} />
                                    </div>
                                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${
                                      activeSubItems[dropItem.name] ? 'rotate-180' : ''
                                    }`} />
                                  </button>
                                  {activeSubItems[dropItem.name] && (
                                    <div className="ml-6 mt-2 space-y-1 bg-gray-50 dark:bg-dark-card rounded-md p-2">
                                      {dropItem.subItems.map((subItem) => (
                                        subItem.href.endsWith('.pdf') ? (
                                          <a
                                            key={subItem.name}
                                            href={subItem.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`block px-4 py-4 text-sm rounded-md transition-colors touch-manipulation ${
                                              isActivePath(subItem.href)
                                                ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'
                                                : isDark
                                                  ? 'text-dark-text-secondary hover:text-blue-400 hover:bg-blue-900/20'
                                                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                                            }`}
                                            onClick={() => {
                                              setIsOpen(false);
                                              setActiveDropdown(null);
                                              setActiveSubItems({});
                                            }}
                                          >
                                            • <TranslatableText text={subItem.name} />
                                          </a>
                                        ) : (
                                          <Link
                                            key={subItem.name}
                                            to={subItem.href}
                                            className={`block px-4 py-4 text-sm rounded-md transition-colors touch-manipulation ${
                                              isActivePath(subItem.href)
                                                ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'
                                                : isDark
                                                  ? 'text-dark-text-secondary hover:text-blue-400 hover:bg-blue-900/20'
                                                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                                            }`}
                                            onClick={() => {
                                              setIsOpen(false);
                                              setActiveDropdown(null);
                                              setActiveSubItems({});
                                            }}
                                          >
                                            • <TranslatableText text={subItem.name} />
                                          </Link>
                                        )
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                /* Handle regular dropdown items */
                                dropItem.href.endsWith('.pdf') ? (
                                  <a
                                    href={dropItem.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`block px-4 py-4 rounded-md text-sm transition-colors touch-manipulation ${
                                      isActivePath(dropItem.href)
                                        ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'
                                        : isDark
                                          ? 'text-dark-text-secondary hover:text-blue-400 hover:bg-blue-900/20'
                                          : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                                    }`}
                                    onClick={() => {
                                      setIsOpen(false);
                                      setActiveDropdown(null);
                                      setActiveSubItems({});
                                    }}
                                  >
                                    <TranslatableText text={dropItem.name} />
                                  </a>
                                ) : (
                                  <Link
                                    to={dropItem.href}
                                    className={`block px-4 py-4 rounded-md text-sm transition-colors touch-manipulation ${
                                      isActivePath(dropItem.href)
                                        ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'
                                        : isDark
                                          ? 'text-dark-text-secondary hover:text-blue-400 hover:bg-blue-900/20'
                                          : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                                    }`}
                                    onClick={() => {
                                      setIsOpen(false);
                                      setActiveDropdown(null);
                                      setActiveSubItems({});
                                    }}
                                  >
                                    <TranslatableText text={dropItem.name} />
                                  </Link>
                                )
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    item.href.endsWith('.pdf') ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center px-3 py-4 rounded-md text-sm font-medium transition-colors touch-manipulation ${
                          isActivePath(item.href)
                            ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'
                            : isDark
                              ? 'text-dark-text hover:text-blue-400 hover:bg-blue-900/20'
                              : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                        onClick={() => {
                          setIsOpen(false);
                          setActiveDropdown(null);
                          setActiveSubItems({});
                        }}
                      >
                        <item.icon className="w-5 h-5 mr-3" />
                        <TranslatableText text={item.name} />
                      </a>
                    ) : (
                      <Link
                        to={item.href}
                        className={`flex items-center px-3 py-4 rounded-md text-sm font-medium transition-colors touch-manipulation ${
                          isActivePath(item.href)
                            ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'
                            : isDark
                              ? 'text-dark-text hover:text-blue-400 hover:bg-blue-900/20'
                              : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                        onClick={() => {
                          setIsOpen(false);
                          setActiveDropdown(null);
                          setActiveSubItems({});
                        }}
                      >
                        <item.icon className="w-5 h-5 mr-3" />
                        <TranslatableText text={item.name} />
                      </Link>
                    )
                  )}
                </div>
              ))}
              
              <div className="touch-manipulation">
                <Link
                  to="/admissions"
                  className="block w-full mt-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-4 rounded-md text-sm font-medium text-center touch-manipulation"
                  onClick={() => {
                    setIsOpen(false);
                    setActiveDropdown(null);
                    setActiveSubItems({});
                  }}
                >
                  <div className="flex items-center justify-center">
                    <AcademicCapIcon className="w-4 h-4 mr-2" />
                    <TranslatableText text="Apply Now" />
                  </div>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;